import { useEffect, useState } from 'react'
import { InvestPaymentMethodType, InvestPaymentMethodTypes } from '../../../types/mf-execution.types'

type UsePaymentMethodAutoSwitchParams = {
  amount: number
  paymentMethods: InvestPaymentMethodType[]
  selectedPaymentMethod: InvestPaymentMethodType | null
  handlePaymentMethodChange: (paymentMethod: InvestPaymentMethodType | null) => void
}

type UsePaymentMethodAutoSwitchReturn = {
  blockBecauseNotAvailablePaymentMethod: boolean
  isUserExplicitSelection: boolean
  setIsUserExplicitSelection: (value: boolean) => void
}

/**
 * Custom hook that handles automatic payment method switching based on amount changes.
 * 
 * Features:
 * - Auto-switches payment method when amount exceeds current method's limit
 * - Auto-optimizes payment method when amount decreases (e.g., switches back to UPI if available)
 * - Respects user's explicit selection - persists user-selected method unless it becomes invalid
 * 
 * Priority order for auto-selection: UPI > Net Banking (never Autopay/MANDATE)
 */
export const usePaymentMethodAutoSwitchLegacy = ({
  amount,
  paymentMethods,
  selectedPaymentMethod,
  handlePaymentMethodChange
}: UsePaymentMethodAutoSwitchParams): UsePaymentMethodAutoSwitchReturn => {
  const [blockBecauseNotAvailablePaymentMethod, setBlockBecauseNotAvailablePaymentMethod] = useState(false)
  const [isUserExplicitSelection, setIsUserExplicitSelection] = useState(false)

  /**
   * Checks if a payment method is valid for a given amount
   * @param paymentMethod - The payment method to check
   * @param checkAmount - The amount to validate against (defaults to current amount)
   * @returns true if the payment method can handle the amount, false otherwise
   */
  const isPaymentMethodValidForAmount = (
    paymentMethod: InvestPaymentMethodType,
    checkAmount: number = amount
  ): boolean => {
    if (!paymentMethod) return false

    // If no maximum limit is set, the method is always valid
    if (!paymentMethod?.maximumAllowedAmount) return true

    // Method is valid if amount is within the maximum allowed
    return checkAmount <= (paymentMethod?.maximumAllowedAmount ?? Infinity)
  }

  /**
   * Gets all payment methods that are eligible for a given amount
   * Excludes MANDATE (Autopay) methods as they should never be auto-selected
   * @param checkAmount - The amount to check eligibility for (defaults to current amount)
   * @returns Array of eligible payment methods
   */
  const getEligiblePaymentMethodsForAmount = (
    checkAmount: number = amount
  ): InvestPaymentMethodType[] => {
    if (!paymentMethods) return []

    return paymentMethods.filter(
      (method) =>
        method?.type !== InvestPaymentMethodTypes.MANDATE &&
        isPaymentMethodValidForAmount(method, checkAmount)
    )
  }

  /**
   * Finds the best payment method for a given amount based on priority:
   * Priority order: UPI > Net Banking (never Autopay/MANDATE)
   * @param checkAmount - The amount to find a payment method for (defaults to current amount)
   * @returns The best suitable payment method, or null if none found
   */
  const findBestPaymentMethodForAmount = (
    checkAmount: number = amount
  ): InvestPaymentMethodType | null => {
    const eligibleMethods = getEligiblePaymentMethodsForAmount(checkAmount)

    if (eligibleMethods?.length === 0) {
      return null
    }

    // Prioritize UPI over Net Banking
    const upiMethod = eligibleMethods.find(
      (method) => method?.type === InvestPaymentMethodTypes.UPI
    )
    if (upiMethod) {
      return upiMethod
    }

    const netBankingMethod = eligibleMethods.find(
      (method) => method?.type === InvestPaymentMethodTypes.NET_BANKING
    )
    if (netBankingMethod) {
      return netBankingMethod
    }

    // If no UPI or Net Banking found, return the first eligible method
    // (though this should rarely happen based on the filter)
    return eligibleMethods?.[0] || null
  }

  /**
   * Auto-adjusts payment method when amount changes
   * Handles both increasing and decreasing amounts:
   * - When amount increases beyond current method's limit: switch to suitable method
   * - When amount decreases: switch back to best method for the new amount range (e.g., UPI if available)
   * 
   * IMPORTANT: Respects user's explicit selection - if user explicitly selected a payment method,
   * it will be persisted unless it becomes invalid for the current amount.
   */
  useEffect(() => {
    // Early return if prerequisites are not met
    if (!paymentMethods || !selectedPaymentMethod || amount <= 0) {
      return
    }

    // Check if current method is valid for the new amount
    const isCurrentMethodValid = isPaymentMethodValidForAmount(selectedPaymentMethod)

    // If user explicitly selected a payment method, don't auto-switch
    if (isUserExplicitSelection) {
      setBlockBecauseNotAvailablePaymentMethod(!isCurrentMethodValid)
      return
    }

    // If current method is invalid, we need to find an alternative (even if user explicitly selected it)
    if (!isCurrentMethodValid) {
      const bestPaymentMethod = findBestPaymentMethodForAmount()

      if (bestPaymentMethod) {
        setBlockBecauseNotAvailablePaymentMethod(false)
        // Reset user explicit selection flag since we're auto-switching due to invalidity
        setIsUserExplicitSelection(false)
        handlePaymentMethodChange(bestPaymentMethod)
      } else {
        // No suitable payment method found for this amount
        setBlockBecauseNotAvailablePaymentMethod(true)
      }
      return
    }

    // Current method is valid but user hasn't explicitly selected it
    // Auto-optimize: check if there's a better option for this amount range
    // (e.g., if amount decreased and UPI is now available, prefer UPI over Net Banking)
    const bestPaymentMethod = findBestPaymentMethodForAmount()

    // Only switch if we found a better method and it's different from current
    if (bestPaymentMethod && bestPaymentMethod?.paymentMethodId !== selectedPaymentMethod?.paymentMethodId) {
      setBlockBecauseNotAvailablePaymentMethod(false)
      handlePaymentMethodChange(bestPaymentMethod)
    } else {
      // Current method is still the best option or no better option found
      setBlockBecauseNotAvailablePaymentMethod(false)
    }
  }, [amount, isUserExplicitSelection, selectedPaymentMethod, paymentMethods, handlePaymentMethodChange])

  return {
    blockBecauseNotAvailablePaymentMethod,
    isUserExplicitSelection,
    setIsUserExplicitSelection
  }
}
